import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-900 border-amber-200",
  confirmed: "bg-emerald-100 text-emerald-900 border-emerald-200",
  completed: "bg-slate-100 text-slate-900 border-slate-200",
  cancelled: "bg-red-100 text-red-900 border-red-200",
};

export default function Bookings() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    const params = { page, per_page: perPage };
    if (statusFilter !== "all") params.status = statusFilter;
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    const { data } = await api.get("/bookings", { params });
    setItems(Array.isArray(data) ? data : data.items || []);
    setTotal(Array.isArray(data) ? data.length : data.total || 0);
    setPages(Array.isArray(data) ? 1 : data.pages || 1);
    setLoading(false);
  };

  useEffect(() => { load(); }, [page, statusFilter, debouncedSearch]);

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Schedule</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">Bookings</h1>
        </div>
        <Link to="/app/bookings/new">
          <Button className="bg-[#1A1A1A] text-white hover:bg-black rounded-full" data-testid="bookings-create-btn">
            <Plus size={16} className="mr-1" /> New booking
          </Button>
        </Link>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            placeholder="Search client, type, location…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 bg-white"
            data-testid="bookings-search-input"
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)}>
          <SlidersHorizontal size={14} className="mr-1" /> Filters
        </Button>
      </div>

      {showFilters && (
        <div className="rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44 bg-white" data-testid="bookings-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="hidden md:block bg-white border border-black/10 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Event Type</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-10">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-10">No bookings yet. <Link to="/app/bookings/new" className="text-[#D85C38] hover:underline">Create your first booking</Link>.</TableCell></TableRow>
            ) : (
              items.map((b) => (
                <TableRow key={b.id} className="cursor-pointer hover:bg-black/[0.02]" data-testid={`booking-row-${b.id}`}>
                  <TableCell className="font-medium">
                    <Link to={`/app/bookings/${b.id}`}>{b.end_date ? `${b.event_date} → ${b.end_date}` : b.event_date}</Link>
                  </TableCell>
                  <TableCell><Link to={`/app/bookings/${b.id}`}>{b.client_name}</Link></TableCell>
                  <TableCell className="text-zinc-600">{b.event_type_name}</TableCell>
                  <TableCell className="text-zinc-600">{b.location || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`capitalize border ${STATUS_COLORS[b.status] || ""}`}>{b.status}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="rounded-2xl border border-black/10 bg-white p-6 text-center text-sm text-zinc-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-black/10 bg-white p-6 text-center text-sm text-zinc-500">No bookings yet.</div>
        ) : (
          items.map((b) => (
            <Link key={b.id} to={`/app/bookings/${b.id}`} className="block rounded-2xl border border-black/10 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{b.client_name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{b.event_type_name}</div>
                </div>
                <Badge variant="outline" className={`capitalize border ${STATUS_COLORS[b.status] || ""}`}>{b.status}</Badge>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-zinc-600">
                <span>{b.end_date ? `${b.event_date} → ${b.end_date}` : b.event_date}</span>
                <span>{b.location || "—"}</span>
              </div>
            </Link>
          ))
        )}
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-500">Showing {items.length ? (page - 1) * perPage + 1 : 0} - {(page - 1) * perPage + items.length} of {total}</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft size={16} />
          </Button>
          <span className="text-sm font-medium">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
